"use client";

import { useState, useEffect, useRef } from "react";
import { hasConsentBeenSet, setConsent } from "@/lib/analytics/consent";
import { logConsent } from "@/lib/analytics/consent-log";

interface CookieConsentProps {
  locale?: "sv" | "en";
}

export default function CookieConsent({ locale = "sv" }: CookieConsentProps) {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  // Förkryssade från början (ägarens beslut 2026-09-24, med vetskap om att
  // EU-domstolen i Planet49 inte räknar förkryssade rutor som samtycke).
  // Att avböja = kryssa ur + "Spara mina val"; ingen separat avböj-knapp.
  const [analytics, setAnalytics] = useState(true);
  const [marketing, setMarketing] = useState(true);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!hasConsentBeenSet()) {
      // Samtycket ligger i webbläsarens lagring. Servern kan inte veta om
      // rutan ska visas, så den får inte avgöras under render — då renderar
      // server och klient olika saker.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setVisible(true);
      logConsent({ type: "shown" });
    }
    const onConsentChanged = () => {
      if (hasConsentBeenSet()) setVisible(false);
    };
    window.addEventListener("uk:consent-changed", onConsentChanged);
    return () => window.removeEventListener("uk:consent-changed", onConsentChanged);
  }, []);

  // Rutan blockerar hela sidan tills ett val är gjort (ägarens beslut
  // 2026-09-24): ingen stängknapp, ingen Esc, sidan bakom går inte att
  // scrolla och Tab stannar i rutan.
  useEffect(() => {
    if (!visible) return;
    const root = document.documentElement;
    const previous = root.style.overflow;
    root.style.overflow = "hidden";
    const card = cardRef.current;
    card?.focus();
    const onTab = (e: KeyboardEvent) => {
      if (e.key !== "Tab" || !card) return;
      const items = Array.from(card.querySelectorAll<HTMLElement>("button, input:not([disabled]), a[href]"));
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (!card.contains(document.activeElement)) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onTab);
    return () => {
      root.style.overflow = previous;
      window.removeEventListener("keydown", onTab);
    };
  }, [visible]);

  if (!visible) return null;

  const sv = locale === "sv";

  const handleAcceptAll = (via: "button" | "check") => {
    setConsent({ analytics: true, marketing: true });
    logConsent({ type: "choice", choice: "accept_all", via, analytics: true, marketing: true });
    setVisible(false);
  };

  const handleSavePreferences = () => {
    setConsent({ analytics, marketing });
    logConsent({ type: "choice", choice: "custom", via: "save", analytics, marketing });
    setVisible(false);
  };

  // z-index över Neuric-chatten (2147483000), så att även chattfältet ligger
  // under rutan tills ett val är gjort.
  return (
    <div className="fixed inset-0 z-[2147483647] flex items-center justify-center bg-black/55 p-4 backdrop-blur-[2px] md:p-6">
      <div
        ref={cardRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={sv ? "Cookies" : "Cookies"}
        className="relative w-full max-w-lg max-h-[85dvh] overflow-y-auto rounded-2xl bg-white shadow-2xl border border-gray-200 p-5 outline-none"
      >
        {/* Genväg för att acceptera: en bock (aldrig ett X — ett X som accepterar är vilseledande). */}
        <button
          onClick={() => handleAcceptAll("check")}
          aria-label={sv ? "Acceptera" : "Accept"}
          className="absolute right-3 top-3 grid h-9 w-9 place-items-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
        >
          <svg aria-hidden="true" viewBox="0 0 16 16" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 8.5l3.2 3L13 4.5" />
          </svg>
        </button>
        <p className="pr-12 text-sm text-gray-700 leading-relaxed">
          {sv
            ? "Vi anv\u00e4nder cookies f\u00f6r att f\u00f6rb\u00e4ttra din upplevelse och analysera trafik."
            : "We use cookies to improve your experience and analyze traffic."}
        </p>

        {showDetails && (
          <div className="mt-4 space-y-3">
            <label className="flex items-center gap-3 text-sm">
              <input type="checkbox" checked disabled className="accent-brand-secondary h-4 w-4" />
              <span className="text-gray-500">
                {sv ? "N\u00f6dv\u00e4ndiga (alltid p\u00e5)" : "Necessary (always on)"}
              </span>
            </label>
            <label className="flex items-center gap-3 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={analytics}
                onChange={(e) => setAnalytics(e.target.checked)}
                className="accent-brand-secondary h-4 w-4"
              />
              <span>{sv ? "Analys" : "Analytics"}</span>
            </label>
            <label className="flex items-center gap-3 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={marketing}
                onChange={(e) => setMarketing(e.target.checked)}
                className="accent-brand-secondary h-4 w-4"
              />
              <span>{sv ? "Marknadsf\u00f6ring" : "Marketing"}</span>
            </label>
          </div>
        )}

        {/* Första lagret: Acceptera + en liten Anpassa-länk. Att avböja =
            kryssa ur under Anpassa och spara (ägarens beslut 2026-09-24, med
            vetskap om att EDPB:s cookie-taskforce ser ett första lager utan
            avböj som ogiltigt samtycke). Samma upplägg som wedrive.se. */}
        <div className="mt-4 flex flex-col gap-2">
          <button
            onClick={() => handleAcceptAll("button")}
            className="min-h-12 rounded-xl bg-brand-secondary px-4 py-2.5 text-sm font-bold text-white hover:brightness-110 transition-all"
          >
            {sv ? "Acceptera" : "Accept"}
          </button>
          {showDetails ? (
            <button
              onClick={handleSavePreferences}
              className="min-h-12 rounded-xl border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-all"
            >
              {sv ? "Spara mina val" : "Save my choices"}
            </button>
          ) : (
            <button
              onClick={() => setShowDetails(true)}
              className="mx-auto px-2 py-0.5 text-[11px] text-gray-300 hover:text-gray-500 focus-visible:text-gray-500 transition-colors"
            >
              {sv ? "Anpassa" : "Customize"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
